import React, { useState } from "react";
import { Table, Spinner } from "react-bootstrap";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import CountryList from "../../CountryList";
import { googleApi } from "../../../services/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import { FaTachometerAlt, FaSearch } from "react-icons/fa";

const AnalyticsAndReporting = () => {
  const [packageName, setPackageName] = useState("");
  const [country, setCountry] = useState("");
  const [analyticsData, setAnalyticsData] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    setError("");
    if (!packageName || !country) {
      setError("Please fill in all fields.");
      return;
    }

    setLoading(true);
    try {
      const [analyticsResponse, kpiResponse] = await Promise.all([
        googleApi.post("app-data/", { package_name: packageName, country }),
        googleApi.post("fetch-kpis/", { package_name: packageName, country }),
      ]);

      setAnalyticsData(analyticsResponse.data);
      setKpiData(kpiResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(
        error?.response
          ? "Failed to fetch data. Please try again."
          : "Failed to make request."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  // ----- CSV (safe for Excel) -----
  const downloadCSV = () => {
    if (!analyticsData || !kpiData) return;

    const headers = [
      "Total Installs",
      "Estimated Uninstall Rate (%)",
      "Average Rating",
      "Total Reviews",
      "Daily Active Users",
      "Monthly Active Users",
      "Retention Rate (%)",
      "Churn Rate (%)",
      "Avg Session Duration",
      "User Acquisition Cost",
      "Lifetime Value",
      "Average Revenue Per User",
      "Total Reviews (KPI)",
      "Average Score",
    ];

    const row = [
      analyticsData["Total Installs"], // may include commas/+
      Number(analyticsData["Estimated Uninstall Rate (rough)"]).toFixed(2),
      Number(analyticsData["Average Rating"]).toFixed(2),
      analyticsData["Total Reviews"], // keep as raw number
      kpiData.daily_active_users,
      kpiData.monthly_active_users,
      kpiData.retention_rate,
      kpiData.churn_rate,
      kpiData.avg_session_duration,
      kpiData.user_acquisition_cost,
      kpiData.lifetime_value,
      kpiData.average_revenue_per_user,
      kpiData.total_reviews,
      kpiData.average_score,
    ];

    const escapeCSV = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const csv =
      "\uFEFF" +
      [headers, row]
        .map((r) => r.map(escapeCSV).join(","))
        .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, "analytics_report.csv");
  };

  // ----- PDF (tables + chart screenshots) -----
  const downloadPDF = async () => {
    if (!analyticsData || !kpiData) return;

    const doc = new jsPDF();
    doc.text("Analytics Report", 14, 16);

    autoTable(doc, {
      startY: 24,
      head: [
        [
          "Total Installs",
          "Estimated Uninstall Rate (%)",
          "Average Rating",
          "Total Reviews",
        ],
      ],
      body: [
        [
          analyticsData["Total Installs"],
          Number(analyticsData["Estimated Uninstall Rate (rough)"]).toFixed(2),
          Number(analyticsData["Average Rating"]).toFixed(2),
          analyticsData["Total Reviews"],
        ],
      ],
      styles: { fontSize: 10 },
    });

    const afterFirst =
      (doc.lastAutoTable && doc.lastAutoTable.finalY) ? doc.lastAutoTable.finalY + 8 : 32;

    autoTable(doc, {
      startY: afterFirst,
      head: [
        [
          "Daily Active Users",
          "Monthly Active Users",
          "Retention Rate (%)",
          "Churn Rate (%)",
          "Avg Session Duration",
          "User Acquisition Cost",
          "Lifetime Value",
          "Average Revenue Per User",
          "Total Reviews (KPI)",
          "Average Score",
        ],
      ],
      body: [
        [
          kpiData.daily_active_users,
          kpiData.monthly_active_users,
          kpiData.retention_rate,
          kpiData.churn_rate,
          kpiData.avg_session_duration,
          kpiData.user_acquisition_cost,
          kpiData.lifetime_value,
          kpiData.average_revenue_per_user,
          kpiData.total_reviews,
          kpiData.average_score,
        ],
      ],
      styles: { fontSize: 10 },
    });

    let y =
      (doc.lastAutoTable && doc.lastAutoTable.finalY)
        ? doc.lastAutoTable.finalY + 10
        : 30;

    const pageW =
      doc.internal.pageSize.getWidth?.() ?? doc.internal.pageSize.width;
    const pageH =
      doc.internal.pageSize.getHeight?.() ?? doc.internal.pageSize.height;

    const addSection = async (selector, title) => {
      const el = document.querySelector(selector);
      if (!el) return;

      // If there's not enough space, add a page
      if (y + 10 > pageH - 30) {
        doc.addPage();
        y = 20;
      }

      doc.setFontSize(12);
      doc.text(title, 14, y);
      y += 4;

      const canvas = await html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const img = canvas.toDataURL("image/png");
      const maxW = pageW - 28; // margins 14/14
      const imgH = (canvas.height * maxW) / canvas.width;

      if (y + imgH > pageH - 20) {
        doc.addPage();
        y = 20;
      }
      doc.addImage(img, "PNG", 14, y, maxW, imgH);
      y += imgH + 8;
    };

    await addSection("#uninstallRateChart", "KPI Line Chart");
    await addSection("#averageRatingChart", "KPI Bar Charts");

    doc.save("analytics_report.pdf");
  };

  const data = [
    {
      name: "Metrics",
      "Daily Active Users": kpiData?.daily_active_users || 0,
      "Monthly Active Users": kpiData?.monthly_active_users || 0,
      "Retention Rate": kpiData?.retention_rate || 0,
      "Churn Rate": kpiData?.churn_rate || 0,
      "Avg Session Duration": kpiData?.avg_session_duration || 0,
      "User Acquisition Cost": kpiData?.user_acquisition_cost || 0,
      "Lifetime Value": kpiData?.lifetime_value || 0,
      "Average Revenue Per User": kpiData?.average_revenue_per_user || 0,
      "Total Reviews": kpiData?.total_reviews || 0,
      "Average Score": kpiData?.average_score || 0,
    },
  ];

  const lineColors = {
    "Daily Active Users": "#8884d8",
    "Monthly Active Users": "#82ca9d",
    "Retention Rate": "#ffc658",
    "Churn Rate": "#ff7300",
    "Avg Session Duration": "#387908",
    "User Acquisition Cost": "#b82500",
    "Lifetime Value": "#00b894",
    "Average Revenue Per User": "#6c63ff",
    "Total Reviews": "#fd79a8",
    "Average Score": "#ffcc00",
  };

  return (
    <div className="container mt-4">
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaTachometerAlt className="mr-3 text-purple-500" />
          KPI Data
        </h2>
        <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-5">
                    <label htmlFor="packageName" className="block text-sm font-medium text-gray-700 mb-1">
                        Enter Package Name
                    </label>
                    <input
                        type="text"
                        className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition duration-200"
                        id="packageName"
                        placeholder="e.g. com.example.app"
                        value={packageName}
                        onChange={(e) => setPackageName(e.target.value)}
                        required
                    />
                </div>
                <div className="md:col-span-5">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Country</label>
                    <CountryList onSelect={(selectedCountry) => setCountry(selectedCountry)} />
                </div>
                <div className="md:col-span-2">
                    <button
                        type="submit"
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-md shadow transition duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        ) : (
                            <FaSearch className="mr-2" />
                        )}
                        {loading ? "Loading..." : "Get KPIs"}
                    </button>
                </div>
            </div>
        </form>
      </div>

      {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "300px" }}
        >
          <Spinner animation="border" role="status" />
          <span className="ms-2">Loading Please Wait...</span>
        </div>
      )}
      {error && <div className="alert alert-danger mt-3">{error}</div>}

      {analyticsData && kpiData && (
        <div className="mt-5">
          <h3 className="mt-5 mb-3" style={{ fontSize: "0.7rem" }}>
            KPI Data
          </h3>
          <Table striped bordered hover style={{ fontSize: "0.7rem" }}>
            <thead>
              <tr>
                <th>Daily Active Users</th>
                <th>Monthly Active Users</th>
                <th>Retention Rate (%)</th>
                <th>Churn Rate (%)</th>
                <th>Avg Session Duration</th>
                <th>User Acquisition Cost</th>
                <th>Lifetime Value</th>
                <th>Average Revenue Per User</th>
                <th>Total Reviews</th>
                <th>Average Score</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{kpiData.daily_active_users}</td>
                <td>{kpiData.monthly_active_users}</td>
                <td>{kpiData.retention_rate}</td>
                <td>{kpiData.churn_rate}</td>
                <td>{kpiData.avg_session_duration}</td>
                <td>{kpiData.user_acquisition_cost}</td>
                <td>{kpiData.lifetime_value}</td>
                <td>{kpiData.average_revenue_per_user}</td>
                <td>{kpiData.total_reviews}</td>
                <td>{kpiData.average_score}</td>
              </tr>
            </tbody>
          </Table>

          <div
            id="uninstallRateChart"
            className="card p-4 mb-7 mt-5"
            style={{
              borderRadius: "12px",
              boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.15)",
              background: "#fff",
            }}
          >
            <h4 className="text-center mb-3">Real-Time KPI Metrics</h4>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={data}
                margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ background: "#f5f5f5", borderRadius: "8px" }}
                />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                {Object.keys(lineColors).map((key) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={lineColors[key]}
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div id="averageRatingChart" className="row d-flex justify-content-center">
            {[
              {
                id: "dailyActiveUsersChart",
                title: "Daily Active Users",
                value: kpiData.daily_active_users,
                color: "#8884d8",
              },
              {
                id: "monthlyActiveUsersChart",
                title: "Monthly Active Users",
                value: kpiData.monthly_active_users,
                color: "#82ca9d",
              },
              {
                id: "retentionRateChart",
                title: "Retention Rate",
                value: kpiData.retention_rate,
                color: "#ffc658",
              },
              {
                id: "churnRateChart",
                title: "Churn Rate",
                value: kpiData.churn_rate,
                color: "#ff7300",
              },
              {
                id: "avgSessionDurationChart",
                title: "Avg Session Duration",
                value: kpiData.avg_session_duration,
                color: "#387908",
              },
              {
                id: "userAcquisitionCostChart",
                title: "User Acquisition Cost",
                value: kpiData.user_acquisition_cost,
                color: "#b82500",
              },
              {
                id: "lifetimeValueChart",
                title: "Lifetime Value",
                value: kpiData.lifetime_value,
                color: "#00b894",
              },
              {
                id: "averageRevenuePerUserChart",
                title: "Average Revenue Per User",
                value: kpiData.average_revenue_per_user,
                color: "#6c63ff",
              },
              {
                id: "totalReviewsChart",
                title: "Total Reviews",
                value: kpiData.total_reviews,
                color: "#fd79a8",
              },
              {
                id: "averageScoreChart",
                title: "Average Score",
                value: kpiData.average_score,
                color: "#ffcc00",
              },
            ].map((chart) => (
              <div key={chart.id} className="col-md-6 mb-4">
                <div
                  className="card p-3"
                  style={{
                    borderRadius: "8px",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                    fontSize: "0.7rem",
                  }}
                >
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={[{ name: chart.title, value: chart.value }]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis
                        width={70}
                        domain={[0, "dataMax + 10"]}
                        tick={{ fontSize: 11, angle: 0 }}
                      />
                      <Tooltip />
                      <Bar dataKey="value" fill={chart.color} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <button
              className="btn btn-primary me-2"
              style={{
                padding: "10px 20px",
                fontSize: "0.7rem",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
              onClick={downloadCSV}
            >
              Download CSV
            </button>
            <button
              className="btn btn-success"
              style={{
                padding: "10px 20px",
                fontSize: "0.7rem",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
              onClick={downloadPDF}
            >
              Download PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsAndReporting;
